import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListFilterPayload } from '@app/helpers';
import {
    ConstData,
    GeneralListDataResponse,
    GetBroadcastHistory,
    ReferralNameID,
    Referral,
    AudienceDedupCheckRequest,
    AudienceDedupCheckResponse,
    ScheduledEditBroadcast,
    ScheduledSendBroadcast,
    SendBroadcastSuccess,
    SendWelcomeMessage,
    ReferralServiceProvider,
    ReferralRequest,
    ReferralUpdateRequest,
    ReferralDetails,
    RegisterNeedResponse,
    ReferralStatusRequest,
    SendBroadcastPreviewData,
    PreviewStartDatePayload,
    TagData,
    BroadcastSentUser,
    ReferralCodeValidatePayload,
    ReferralCodeValidateResponse,
    ReferralConfirmPayload,
    ServiceDownBannerData,
    AccountLimitsData,
    AccountFeatureLimitsData,
    SurveyQuestionFormModel,
    SurveyConditionalLogicModel,
    CreateTagData
} from '@app/models';
import { ExternalRefCommentData, ExternalRefCommentRequest } from '@app/models/external-referral';
import { ISegmentAnalyzerData, ISegmentAnalyzerRiskAcceptPayload } from '@app/models/segment-analyzer.model';
import { UserLabel } from '@app/models/label.model';
import { OrganizationList, ReportData, SeniorCenter, TranslatedTextPayload, OrganizationFilters, OrganizationAddressResponse, StoreImageFileRequest, StoreImageFileResponse } from '@app/models/organization.model';
import { CurrentUser, IntercomAccessRequest, IntercomAccessResponse, PortalUser, PortalUserListItem } from '@app/models/profile.model';
import { GetOrgProgramList, GetRemindersList, ProgramRequest, ProgramResponse, ReminderRequest } from '@app/models/program.model';
import { SelfServiceProgram, OrgServiceTypesResponse, CreateServiceData, CreateProgramData, ProgramUsersResponse, TimezoneResponse, UpdateProgramUsersPayload, OrgUserForProgramResponse } from '@app/models/program-management.model';
import {
    GetSurveyHistory,
    GetSurveyHistoryDetail,
    GetSurveyInstanceHistoryDetail,
    GetSurveyList,
    GetSurveyQuestionsList,
    GetUpcomingScheduledSurveyResponse,
    SuggestMessageResponse,
    SurveyQuestionOrderRequestModel,
    SurveyQuestionRequest,
    SurveyQuestionResponse,
    SurveyQuestionResponses,
    SurveyRequest,
    SurveyRerunCountsData,
    SurveyResponse,
    SurveyScheduled,
    SurveyScheduledRequest,
    SurveySendRequest,
    SurveySendResponse,
    SurveySentUser
} from '@app/models/survey.model';
import {
    GetOrgUserList,
    UserInterestResponse,
    GeneralResponse,
    GetUserResponse,
    AddUserResponse,
    UpdateUserResponse,
    AddUser,
    UpdateUser,
    BulkUploadResponse,
    BulkUploadBody,
    GeneralDataResponse,
    DownloadUsersPayload,
    OrgUserData,
    UserInterestPost,
    UserInterestDetail,
    UserInterestAssociatedWarn,
    BulkInterestStatusPayload,
    BulkInterestStatusResponse,
    ScheduledCommsCheckResponse,
    InterestDuplicateCheckPayload,
    UpdateOrgInterestPayload,
    OrgInterestScheduledCommunications
} from '@app/models/user.model';
import { CreateWorkflowReq, PatchWorkflowReq, UpdateWorkflowReq, WorkflowListRecordModel, WorkflowDataModel, WfSurveyWorkflowExists, WfNodeData, TestWorkflowReq, WfPublishPreviewReq, WorkflowVersionDataModel, WfValidationResponse } from '@app/models/workflows.model';
import { appValues, AnalyticAPIReqLabels } from '@app/shared';
import { TableauJWTResponse } from '@app/models/tableau-dashboard,model';
import { environment } from '@environments/environment';
import { Observable, of } from 'rxjs';
import { CreateCustomField, CreateCustomFieldPayload, CustomField, CustomFieldsScheduledCommunicationResponse, UpdateCustomFieldStatus } from '@app/models/custom-fields.model';
import { Language, LanguageActionResponse, UpdateOrganizationLanguagePayload } from '@app/models/language.model';
import { SetupVoiceGreetingRule, StoreAudioFileRequest, StoreAudioFileResponse } from '@app/models/voice-greeting.model';
import { SaveActivationSetupRulePayload, OrgActivationSetupResponse, ActivatedUsersResponse, InactiveUsersCountResponse } from '@app/models/activate-users.model';
import { AuditUserEmail, AuditSection, AuditLogsResponse } from '@app/models/history-and-audit-logs.model';
import { WfAnalyticsReportRequest } from '@app/pages/workflows/services/workflow-analytics.models';
import { TransferLabelUsersPayload } from '@app/models/label-management.model';
import { TransferInterestUsersPayload } from '@app/models/interest-management.model';

@Injectable()
export class HttpService {
    constructor(private readonly http: HttpClient) { }

    baseUrl = environment.apiBaseUrl;
    baseUrlV2 = environment.apiBaseUrlV2;

    validateFileType(payload: any, options: any = {}): Observable<any> {
        return this.http.post(`${this.baseUrl}/validate-file`, payload, options);
    }

    getConstData(key: string): Observable<GeneralDataResponse<ConstData>> {
        return this.http.get<GeneralDataResponse<ConstData>>(`${this.baseUrl}/constant/${key}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getConstData }
        });
    }

    getAllTagData(): Observable<GeneralDataResponse<TagData[]>> {
        return this.http.get<GeneralDataResponse<TagData[]>>(`${this.baseUrl}/tags`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getAllTagData }
        });
    }

    createTagData(body: CreateTagData): Observable<GeneralDataResponse<TagData>> {
        return this.http.post<GeneralDataResponse<TagData>>(`${this.baseUrl}/tags`,
            body, { headers: { 'method-name': AnalyticAPIReqLabels.createTagData } }
        );
    }

    getCustomFields(qParams: HttpParams): Observable<GeneralDataResponse<CustomField[]>> {
        return this.http.get<GeneralDataResponse<CustomField[]>>(`${this.baseUrl}/custom-fields`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getCustomFields },
            params: qParams
        });
    }

    createCustomField(body: CreateCustomFieldPayload): Observable<GeneralDataResponse<CustomField>> {
        return this.http.post<GeneralDataResponse<CustomField>>(`${this.baseUrl}/custom-fields`,
            body, { headers: { 'method-name': AnalyticAPIReqLabels.createCustomField } }
        );
    }

    updateCustomField(id: number, body: CreateCustomField): Observable<GeneralDataResponse<CustomField>> {
        return this.http.put<GeneralDataResponse<CustomField>>(`${this.baseUrl}/custom-fields/${id}`,
            body, { headers: { 'method-name': AnalyticAPIReqLabels.updateCustomField } }
        );
    }

    updateCustomFieldStatus(body: UpdateCustomFieldStatus): Observable<GeneralDataResponse<CustomField[]>> {
        return this.http.patch<GeneralDataResponse<CustomField[]>>(`${this.baseUrl}/custom-fields/status`,
            body, { headers: { 'method-name': AnalyticAPIReqLabels.updateCustomFieldStatus } }
        );
    }

    getCustomFieldsScheduledCommunication(orgId: number, fieldIds: number[]): Observable<GeneralDataResponse<CustomFieldsScheduledCommunicationResponse>> {
        return this.http.post<GeneralDataResponse<CustomFieldsScheduledCommunicationResponse>>(
            `${this.baseUrl}/custom-fields/scheduledCommunications`,
            { orgId, fieldIds },
            { headers: { 'method-name': AnalyticAPIReqLabels.getCustomFieldsScheduledCommunication } }
        );
    }

    generateBulkUploadSampleFiles(orgCode: string, orgId: number, snap: boolean, medicaid: boolean): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/generate-bulk-upload-files/${orgCode}`,
            { orgId, snap, medicaid },
            { headers: { 'method-name': AnalyticAPIReqLabels.generateBulkUploadSampleFiles } }
        );
    }

    getCurrentUser(): Observable<GeneralDataResponse<CurrentUser>> {
        return this.http.get<GeneralDataResponse<CurrentUser>>(`${this.baseUrl}/auth/getCurrentUser`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getCurrentUser }
        });
    }

    acceptTermsPolicy(): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/auth/acceptDocuments`,
            {},
            { headers: { 'method-name': AnalyticAPIReqLabels.getCurrentUser } }
        );
    }

    getOrganizations(params: HttpParams, methodName?: string): Observable<GeneralDataResponse<OrganizationList[]>> {
        return this.http.get<GeneralDataResponse<OrganizationList[]>>(`${this.baseUrl}/dashboardAdmin/organizations`, {
            headers: { 'method-name': methodName ?? AnalyticAPIReqLabels.getOrganizations },
            params
        });
    }

    getSeniorcenterByOrgId(orgId: number): Observable<GeneralDataResponse<SeniorCenter[]>> {
        return this.http.get<GeneralDataResponse<SeniorCenter[]>>(`${this.baseUrl}/dashboardAdmin/getCentersByOrgId/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSeniorcenterByOrgId }
        });
    }

    getOrganizationLanguages(orgId: number): Observable<GeneralDataResponse<string[]>> {
        return this.http.get<GeneralDataResponse<string[]>>(`${this.baseUrl}/language/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrganizationLanguages }
        });
    }

    getOrganizationFiltersById(orgId: number, broadcast_type?: string): Observable<GeneralDataResponse<OrganizationFilters>> {
        const baseUrl = `${this.baseUrl}/dashboardAdmin/organizations/${orgId}/filters`;
        const url = broadcast_type ? `${baseUrl}?broadcast_type=${broadcast_type}` : baseUrl;
        return this.http.get<GeneralDataResponse<OrganizationFilters>>(url, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrganizationFilters }
        });
    }

    translateTextByLanguage(payload: TranslatedTextPayload): Observable<GeneralDataResponse<string>> {
        return this.http.post<GeneralDataResponse<string>>(`${this.baseUrl}/language/translate-text`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrganizationLanguages }
        });
    }

    getDashboardReport(orgId: number): Observable<GeneralDataResponse<ReportData[]>> {
        return this.http.get<GeneralDataResponse<ReportData[]>>(`${this.baseUrl}/reports/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReports }
        });
    }

    getServiceDownBanner(): Observable<GeneralDataResponse<ServiceDownBannerData[]>> {
        return this.http.get<GeneralDataResponse<ServiceDownBannerData[]>>(`${this.baseUrl}/service-status`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getServiceDownBanner }
        });
    }

    //********************BROADCAST********************//
    sendBroadcast(body: ScheduledSendBroadcast | FormData): Observable<SendBroadcastSuccess> {
        if (body instanceof FormData) {
            return this.http.post<SendBroadcastSuccess>(`${this.baseUrl}/broadcast/sendBroadcast`, body, {
                headers: {
                    'method-name': AnalyticAPIReqLabels.sendBroadcast
                }
            });
        } else {
            // Send as JSON if no attachment
            return this.http.post<SendBroadcastSuccess>(`${this.baseUrl}/broadcast/sendBroadcast`, body, {
                headers: {
                    'Content-Type': 'application/json',
                    'method-name': AnalyticAPIReqLabels.sendBroadcast
                }
            });
        }
    }

    getSuggestMessage(prompt: string, broadcastType: string = 'broadcast'): Observable<GeneralDataResponse<SuggestMessageResponse>> {
        return this.http.post<GeneralDataResponse<SuggestMessageResponse>>(
            `${this.baseUrl}/shared/suggestMessage`,
            { prompt, broadcastType },
            { headers: { 'method-name': AnalyticAPIReqLabels.getSuggestMessage } }
        );
    }

    sendBroadcastPreview(body: ScheduledSendBroadcast): Observable<SendBroadcastPreviewData> {
        return this.http.post<SendBroadcastPreviewData>(`${this.baseUrl}/broadcast/broadcastpreview`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.sendBroadcastPreview }
        });
    }

    sendWelcomeMessage(body: SendWelcomeMessage): Observable<SendBroadcastSuccess> {
        return this.http.post<SendBroadcastSuccess>(`${this.baseUrl}/welcomeMessage/send`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.sendWelcomeMessage }
        });
    }

    getBroadcastHistory(orgId: number, params?: HttpParams, isRefresh: boolean = false): Observable<GetBroadcastHistory> {
        const schBroadcastLabel = !params?.get('scheduled')
            ? AnalyticAPIReqLabels.getBroadcastHistory
            : AnalyticAPIReqLabels.getScheduledBroadcast;

        return this.http.get<GetBroadcastHistory>(`${this.baseUrl}/broadcast/${orgId}`, {
            headers: {
                'method-name': isRefresh ? AnalyticAPIReqLabels.getBroadcastHistoryRefresh : schBroadcastLabel
            },
            params: params
        });
    }

    downloadBroadcastAttachment(body: any): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/broadcast/download-attachment`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.downloadBroadcastAttachment },
            responseType: 'blob' as 'json'
        });
    }

    getBroadcastSentUsers(broadcastId: number, params?: HttpParams): Observable<GeneralListDataResponse<BroadcastSentUser>> {
        return this.http.get<GeneralListDataResponse<BroadcastSentUser>>(`${this.baseUrl}/broadcast/sentusers/${broadcastId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getBroadcastSentUsers },
            params: params
        });
    }

    getBroadcastDataById(broadcastId: number): Observable<GeneralDataResponse<ScheduledEditBroadcast>> {
        return this.http.get<GeneralDataResponse<ScheduledEditBroadcast>>(`${this.baseUrl}/broadcast/detail/${broadcastId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getBroadcastDataById }
        });
    }

    getAudienceDedupCheck(body: AudienceDedupCheckRequest): Observable<AudienceDedupCheckResponse> {
        return this.http.post<AudienceDedupCheckResponse>(
            `${this.baseUrl}/broadcast/checkAudienceDedup`,
            body,
            { headers: { 'method-name': AnalyticAPIReqLabels.checkAudienceDedup } }
        );
    }

    updateBroadcast(broadcastId: number, body: ScheduledSendBroadcast | FormData): Observable<GeneralDataResponse<any>> {
        return this.http.put<GeneralDataResponse<any>>(`${this.baseUrl}/broadcast/${broadcastId}`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateBroadcast }
        });
    }

    deleteBroadcast(broadcastId: number): Observable<GeneralDataResponse<any>> {
        return this.http.delete<GeneralDataResponse<any>>(`${this.baseUrl}/broadcast/${broadcastId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteBroadcast }
        });
    }

    /**Get users for a Organization and senior center with params
     * Converting params into payload object to map new API method
     * to reduce the component level changes.
     */
    getOrgUsersWithParams(orgId: number, params?: HttpParams, broadcastType?: string): Observable<GetOrgUserList> {
        let requestPayload: any = {};

        if (broadcastType && broadcastType === appValues.welcomeMessageParams) {
            requestPayload.active = 'false';
        }

        if (params?.keys()?.length) {
            params.keys().forEach(paramKey => {
                requestPayload[paramKey] = params.get(paramKey);
            });
        }
        if (requestPayload?.programIds?.length) {
            requestPayload = {
                ...requestPayload,
                programIds: requestPayload.programIds.includes(',') ? requestPayload.programIds.split(',') : [requestPayload.programIds]
            };
        }
        return this.getAllOrgUsersV2(orgId, requestPayload);
    }

    // Get users for a Organization and senior center with filters
    getOrgUsersWithFilters(orgId: number, reqPayload: ListFilterPayload = {}): Observable<GetOrgUserList> {
        return this.http.post<GetOrgUserList>(`${this.baseUrl}/users/${orgId}`, reqPayload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgUsersWithFilters }
        });
    }

    // Get available Interests for user
    getUserInterests(orgId: number): Observable<UserInterestResponse> {
        return this.http.get<UserInterestResponse>(`${this.baseUrl}/interests/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUserInterests }
        });
    }

    getInterestsDetailsByOrgId(orgId: number, params?: HttpParams): Observable<GeneralListDataResponse<UserInterestDetail>> {
        return this.http.get<GeneralListDataResponse<UserInterestDetail>>(`${this.baseUrl}/interests/organization/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getInterestsDetailsByOrgId },
            params: params
        });
    }

    getUsersInInterest(orgId: number, interestId: number, params?: HttpParams): Observable<GeneralListDataResponse<any>> {
        return this.http.get<GeneralListDataResponse<any>>(`${this.baseUrl}/interests/users/${interestId}/organization/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUsersInInterest },
            params: params
        });
    }

    updateInterestStatus(interestId: number, is_active: boolean): Observable<GeneralDataResponse<any>> {
        return this.http.put<GeneralDataResponse<any>>(`${this.baseUrl}/org-interest/${interestId}/status`, { is_active });
    }

    bulkUpdateInterestStatus(payload: BulkInterestStatusPayload): Observable<GeneralDataResponse<BulkInterestStatusResponse>> {
        return this.http.put<GeneralDataResponse<BulkInterestStatusResponse>>(`${this.baseUrl}/org-interest/bulk-status`, payload);
    }

    checkScheduledCommunications(orgId: number, interestIds: number[], clientIds: number[] = []): Observable<GeneralDataResponse<ScheduledCommsCheckResponse>> {
        return this.http.post<GeneralDataResponse<ScheduledCommsCheckResponse>>(`${this.baseUrl}/interests/check-scheduled/${orgId}`, { interestIds, clientIds });
    }

    checkInterestNameDuplicate(orgId: number, payload: InterestDuplicateCheckPayload): Observable<GeneralDataResponse<{ exists: boolean }>> {
        return this.http.post<GeneralDataResponse<{ exists: boolean }>>(`${this.baseUrl}/interests/check-duplicate/${orgId}`, payload);
    }

    updateOrgInterests(interests: UpdateOrgInterestPayload[]): Observable<GeneralDataResponse<{ updatedCount: number; skippedCount: number }>> {
        return this.http.put<GeneralDataResponse<{ updatedCount: number; skippedCount: number }>>(`${this.baseUrl}/org-interests`, interests);
    }

    getActiveOrgInterests(orgId: number): Observable<GeneralDataResponse<any[]>> {
        return this.http.get<GeneralDataResponse<any[]>>(`${this.baseUrl}/active-org-interests`, { params: { orgId: orgId.toString() } });
    }

    getOrgInterestsScheduledCommunications(orgInterestIds: number[]): Observable<GeneralDataResponse<OrgInterestScheduledCommunications>> {
        return this.http.get<GeneralDataResponse<OrgInterestScheduledCommunications>>(
            `${this.baseUrl}/org-interests/scheduledCommunications`,
            { params: { orgInterestIds: orgInterestIds.join(',') } }
        );
    }

    getOrganizationLanguage(orgId: number): Observable<GeneralDataResponse<{ language: Language[] }>> {
        return this.http.get<GeneralDataResponse<{ language: Language[] }>>(`${this.baseUrl}/getOrgLanguage/${orgId}`);
    }

    getOrgLanguageWithAudio(orgId: number, params?: any): any {
        const url = `${this.baseUrl}/selfServiceManagement/voiceGreeting/getOrgLanguageWithAudioURL/${orgId}`;
        return this.http.get<GeneralDataResponse<any>>(url, { params });
    }

    getGreetingRuleByOrgId(orgId: number): Observable<any> {
        return this.http.get<GeneralDataResponse<any>>(`${this.baseUrl}/selfServiceManagement/voiceGreeting/retrieveGreetingRule/${orgId}`);
    }

    setupVoiceGreetingRule(payload: SetupVoiceGreetingRule): Observable<any> {
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/setupVoiceGreetingRule`,
            payload
        );
    }

    addFallbackAudio(body: any) {
        return this.http.post(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/addFallbackAudio`,
            body
        );
    }

    removeAudio(orgId: number, params: { languageName: string }): any {
        return this.http.delete<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/deleteAudioFile/${orgId}`,
            { params }
        );
    }

    removeOverrideAudio(params: any) {
        return this.http.delete<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/deleteOverrideAudio`,
            { params }
        );
    }

    getOrgLanguageWithAudioURL(orgId: number, params: any) {
        return this.http.get<any>(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/getOrgLanguageWithAudioURL/${orgId}`,
            { params }
        );
    }

    storeAudioFile(orgId: number, payload: StoreAudioFileRequest): Observable<StoreAudioFileResponse> {
        return this.http.post<StoreAudioFileResponse>(
            `${this.baseUrl}/selfServiceManagement/voiceGreeting/storeAudioFile/${orgId}`,
            payload
        );
    }

    updateOrganizationLanguage(payload:UpdateOrganizationLanguagePayload): Observable<GeneralDataResponse<LanguageActionResponse>> {
        return this.http.post<GeneralDataResponse<LanguageActionResponse>>(`${this.baseUrl}/languageActions`, payload);
    }

    getOrgActivationSetup(orgId: number): Observable<OrgActivationSetupResponse> {
        return this.http.get<OrgActivationSetupResponse>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/retrieveActiveUsersSetupRule/${orgId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getOrgActivationSetup } }
        );
    }

    getProgramActivationSetup(orgId: number, progId: number): Observable<OrgActivationSetupResponse> {
        return this.http.get<OrgActivationSetupResponse>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/retrieveActiveUsersSetupRule/${orgId}/${progId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getProgramActivationSetup } }
        );
    }

    saveActivationSetupRule(payload: SaveActivationSetupRulePayload): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/activateUsersSetupRule`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.saveActivationSetupRule } }
        );
    }

    retrieveActivatedUsers(orgId: number, params?: HttpParams): Observable<ActivatedUsersResponse> {
        return this.http.get<ActivatedUsersResponse>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/retieveActivatedUsers/${orgId}`,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.retieveActivatedUsers },
                params: params
            }
        );
    }

    retrieveInactiveUsersCount(orgId: number, params: HttpParams): Observable<InactiveUsersCountResponse> {
        return this.http.get<InactiveUsersCountResponse>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/retriveNoActiveUsers/${orgId}`,
            {
                params,
                headers: { 'method-name': AnalyticAPIReqLabels.retriveNoActiveUsers }
            }
        );
    }

    downloadActivatedUsers(orgId: number, params?: HttpParams): Observable<Blob> {
        return this.http.get<Blob>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/downloadActivatedUsers/${orgId}`,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.downloadActivatedUsers },
                params: params,
                responseType: 'blob' as 'json'
            }
        );
    }

    triggerManualActivation(orgId: number, params: HttpParams): Observable<GeneralResponse> {
        return this.http.get<GeneralResponse>(
            `${this.baseUrl}/selfServiceManagement/activeUsers/triggerManuallyNonActiveUsers/${orgId}`,
            { 
                params,
                headers: { 'method-name': AnalyticAPIReqLabels.triggerManualActivation } 
            }
        );
    }

    retrievePortalUsersEmails(orgId: number): Observable<AuditUserEmail[]> {
        return this.http.get<AuditUserEmail[]>(`${this.baseUrl}/selfServiceManagement/historyAndAuditLogs/retrievePortalUsersEmails/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.retrievePortalUsersEmails }
        });
    }

    retrieveAuditSections(orgId: number): Observable<AuditSection[]> {
        return this.http.get<AuditSection[]>(`${this.baseUrl}/selfServiceManagement/historyAndAuditLogs/retrieveAuditSection/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.retrieveAuditSections }
        });
    }

    retrieveAuditLogs(orgId: number, params?: HttpParams): Observable<AuditLogsResponse> {
        return this.http.get<AuditLogsResponse>(`${this.baseUrl}/selfServiceManagement/historyAndAuditLogs/retriveAuditLogs/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.retrieveAuditLogs },
            params: params
        });
    }

    downloadAuditLogs(orgId: number, params?: HttpParams): Observable<Blob> {
        return this.http.get<Blob>(`${this.baseUrl}/selfServiceManagement/historyAndAuditLogs/downloadAuditLogs/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.downloadAuditLogs },
            params: params,
            responseType: 'blob' as 'json'
        });
    }

    getUsersInInterestNoPagination(orgId: number, interestId: number, params?: HttpParams): Observable<GeneralListDataResponse<any>> {
        return this.http.get<GeneralListDataResponse<any>>(
            `${this.baseUrl}/interests/users/${interestId}/organization/${orgId}?withPagination=false`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getUsersInInterestNoPagination }, params: params }
        );
    }

    createInterest(data: UserInterestPost): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/interest`, data, {
            headers: { 'method-name': AnalyticAPIReqLabels.createInterest }
        });
    }

    getInterestAssociationsByOrgId(interestId: number, orgId: number): Observable<GeneralDataResponse<UserInterestAssociatedWarn>> {
        return this.http.get<GeneralDataResponse<UserInterestAssociatedWarn>>(
            `${this.baseUrl}/interests/association/${interestId}/organization/${orgId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getInterestAssociationsByOrgId } }
        );
    }

    getPortalUsersByOrgId(orgId: number, qparams?: HttpParams): Observable<GeneralListDataResponse<PortalUserListItem>> {
        return this.http.get<GeneralListDataResponse<PortalUserListItem>>(`${this.baseUrl}/portalUsers/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getPortalUsersByOrgId },
            params: qparams
        });
    }

    createPortalUsers(payload: PortalUser): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/auth/createUser`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.createPortalUsers }
        });
    }

    updatePortalUsers(payload: PortalUser): Observable<GeneralResponse> {
        return this.http.patch<GeneralResponse>(`${this.baseUrl}/portalUsers`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updatePortalUsers }
        })
    }

    resendActivationLink(email: string): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/auth/resendActivationLink`,
            { email },
            { headers: { 'method-name': AnalyticAPIReqLabels.createPortalUsers } }
        );
    }

    getAccountLimits(orgId: number): Observable<AccountLimitsData> {
        return this.http.get<AccountLimitsData>(`${this.baseUrl}/plan/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getAccountLimits }
        });
    }

    getAccountFeatureLimits(orgId: number, featureName: string): Observable<AccountFeatureLimitsData> {
        return this.http.get<AccountFeatureLimitsData>(`${this.baseUrl}/plan/${orgId}/${featureName}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getAccountFeatureLimits }
        });
    }

    unlockAccount(orgId: number, email: string): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/portalUsers/unlock/${orgId}`,
            { email },
            { headers: { 'method-name': AnalyticAPIReqLabels.unlockAccount } }
        );
    }

    requestPortalUserForgotPassword(email: string): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/portalUsers/forgotPassword`,
            { email },
            { headers: { 'method-name': AnalyticAPIReqLabels.portalUserForgotPassword } }
        );
    }

    updatePortalUserStatus(orgId: number, email: string, status: string): Observable<GeneralResponse> {
        return this.http.patch<GeneralResponse>(`${this.baseUrl}/portalUsers/status`, { email, orgId, status }, {
            headers: { 'method-name': AnalyticAPIReqLabels.updatePortalUserStatus }
        });
    }

    getProgramsByOrgId(orgId: number, params?: HttpParams): Observable<GeneralListDataResponse<SelfServiceProgram>> {
        return this.http.get<GeneralListDataResponse<SelfServiceProgram>>(
            `${this.baseUrl}/selfServiceManagement/programs/getOrgPrograms/${orgId}`,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.getProgramsByOrgId },
                params: params
            }
        );
    }

    getOrganizationTimezones(orgId: number): Observable<TimezoneResponse[]> {
        return this.http.get<TimezoneResponse[]>(
            `${this.baseUrl}/selfServiceManagement/programs/getTimezones/${orgId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getOrganizationTimezones } }
        );
    }

    getOrgServiceTypes(orgId: number): Observable<OrgServiceTypesResponse> {
        return this.http.get<OrgServiceTypesResponse>(
            `${this.baseUrl}/selfServiceManagement/programs/getServiceType/${orgId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getOrgServiceTypes } }
        );
    }

    createServiceData(orgId: number, payload: CreateServiceData): Observable<GeneralDataResponse<CreateServiceData>> {
        return this.http.post<GeneralDataResponse<CreateServiceData>>(
            `${this.baseUrl}/selfServiceManagement/programs/insertServiceType/${orgId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.createServiceData } }
        );
    }

    addSelfServiceProgram(orgId: number, payload: CreateProgramData): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/selfServiceManagement/programs/insertOrgProgram/${orgId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.addSelfServiceProgram } }
        );
    }

    updateSelfServiceProgram(orgId: number, programId: number, payload: CreateProgramData): Observable<GeneralResponse> {
        return this.http.patch<GeneralResponse>(
            `${this.baseUrl}/selfServiceManagement/programs/updateOrgProgram/${orgId}/${programId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.updateSelfServiceProgram } }
        );
    }

    getProgramUsers(orgId: number, programId: number, params?: HttpParams): Observable<GeneralListDataResponse<ProgramUsersResponse>> {
        return this.http.get<GeneralListDataResponse<ProgramUsersResponse>>(
            `${this.baseUrl}/selfServiceManagement/programs/getProgramSpecificUsers/${orgId}/${programId}`,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.getProgramUsers },
                params
            }
        );
    }

    getOrgUsersForProgram(orgId: number, params?: HttpParams): Observable<GeneralListDataResponse<OrgUserForProgramResponse>> {
        return this.http.get<GeneralListDataResponse<OrgUserForProgramResponse>>(
            `${this.baseUrl}/selfServiceManagement/programs/getOrgUsers/${orgId}`,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.getProgramUsers },
                params
            }
        );
    }

    getTransferProgramOptions(orgId: number, programId: string): Observable<GeneralDataResponse<any>> {
        return this.http.get<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/programs/getProgramsAndInterestAndLabels/${orgId}/${programId}`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getTransferProgramOptions } }
        );
    }

    transferProgramUsers(orgId: number, programId: string, payload: any): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/programs/transferUsers/${orgId}/${programId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.transferProgramUsers } }
        );
    }

    transferLabelUsers(orgId: number, labelId: string, payload: TransferLabelUsersPayload[]): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/labels/transferUsers/${orgId}/${labelId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.transferLabelUsers } }
        );
    }

    transferInterestUsers(orgId: number, interestId: string, payload: TransferInterestUsersPayload[]): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/interests/transferUsers/${orgId}/${interestId}`,
            payload,
            { headers: { 'method-name': AnalyticAPIReqLabels.transferInterestUsers } }
        );
    }

    updateProgramUsers(orgId: number, programId: number, payload: UpdateProgramUsersPayload): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(
            `${this.baseUrl}/selfServiceManagement/programs/updateProgramUsers/${orgId}/${programId}`,
            payload,
            {
                headers: { 'method-name': AnalyticAPIReqLabels.updateProgramUsers }
            }
        );
    }

    // Get users By Member Id
    getUserByMemberId(orgId: number, center: number, memberId: string): Observable<GetUserResponse> {
        return this.http.get<GetUserResponse>(`${this.baseUrl}/users/${orgId}/${center}/${memberId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUserByMemberId }
        });
    }
    // Add users to senior center
    postNewUser(newUser: AddUser): Observable<AddUserResponse> {
        return this.http.post<AddUserResponse>(`${this.baseUrl}/users`, newUser, {
            headers: { 'method-name': AnalyticAPIReqLabels.postNewUser }
        });
    }
    // Update User
    updateUser(memberId: string, newUser: UpdateUser): Observable<UpdateUserResponse> {
        return this.http.put<UpdateUserResponse>(`${this.baseUrl}/users/${memberId}`, newUser, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateUser }
        });
    }
    // Delete User
    deleteUserByMemberId(memberId: string): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrl}/users/${memberId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteUserByMemberId }
        });
    }
    // Get User download link
    getUserDownloadLink(organizationId: number, downloadPayload: DownloadUsersPayload) {
        return this.http.post(`${this.baseUrl}/users/download/${organizationId}`, downloadPayload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUserDownloadLink },
            responseType: 'blob' as 'json'
        });
    }

    // Add users to senior center
    userBulkUpload(orgId: number, centerId: number, body: BulkUploadBody): Observable<BulkUploadResponse> {
        return this.http.post<BulkUploadResponse>(`${this.baseUrl}/users/bulkupload/${orgId}/${centerId}`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.userBulkUpload }
        });
    }

    /** SINGLE USER ARCHITECTURE API's */
    getAllOrgUsersV2(orgId: number, reqPayload: ListFilterPayload = {}): Observable<GetOrgUserList> {
        return this.http.post<GetOrgUserList>(`${this.baseUrlV2}/users/${orgId}`, reqPayload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getAllOrgUsersV2 }
        });
    }

    getUserByOrgMemberIdV2(orgId: number, memberId: string): Observable<GetUserResponse> {
        return this.http.get<GetUserResponse>(`${this.baseUrlV2}/users/${orgId}/${memberId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUserByOrgMemberIdV2 }
        });
    }

    addOrgUserV2(userData: OrgUserData): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrlV2}/users`, userData, {
            headers: { 'method-name': AnalyticAPIReqLabels.addOrgUserV2 }
        });
    }

    updateOrgUserV2(memberId: string, userData: OrgUserData): Observable<GeneralDataResponse<any>> {
        return this.http.put<GeneralDataResponse<any>>(`${this.baseUrlV2}/users/${memberId}`, userData, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateOrgUserV2 }
        });
    }

    deleteUserByMemberIdV2(memberId: string): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrlV2}/users/${memberId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteUserByMemberId }
        });
    }

    updateRemoveClientInterests(payload: any, orgId: number): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrlV2}/users/modify-interests/${orgId}`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateRemoveClientInterests }
        });
    }

    getUserDownloadLinkV2(organizationId: number, downloadPayload: DownloadUsersPayload) {
        return this.http.post(`${this.baseUrlV2}/users/download/${organizationId}`, downloadPayload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUserDownloadLinkV2 },
            responseType: 'blob' as 'json'
        });
    }

    userBulkUploadV2(orgCode: string, body: BulkUploadBody): Observable<BulkUploadResponse> {
        if (body?.file) {
            const formData: FormData = new FormData();
            formData.append('file', body.file);
            if (body.source) {
                formData.append('source', body.source);
            }
            return this.http.post<BulkUploadResponse>(`${this.baseUrl}/bulk-user-upload/${orgCode}`, formData, {
                headers: { 'method-name': AnalyticAPIReqLabels.userBulkUploadV2 }
            });
        } else {
            return this.http.post<BulkUploadResponse>(`${this.baseUrl}/bulk-user-upload/${orgCode}`, body, {
                headers: { 'method-name': AnalyticAPIReqLabels.userBulkUploadV2 }
            });
        }
    }

    bulkUploadStatusV2(orgCode: string, params?: HttpParams): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/bulk-user-upload/${orgCode}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.userBulkUploadV2 }, 
            params
        });
    }

    bulkUploadCsvV2(id: string, orgCode: string, type: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/bulk-user-upload/get-csv/${orgCode}/${id}/${type}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.userBulkUploadV2 }
        });
    }

    getBulkUploadFilesAsblob(id: string, orgCode: string, type: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/bulk-user-upload/get-csv-blob/${orgCode}/${id}/${type}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.userBulkUploadV2 },
            responseType: 'blob'
        });
    }

    //********************PROGRAM REMINDERS********************//
    getOrgPrograms(orgId: number): Observable<GetOrgProgramList> {
        return this.http.get<GetOrgProgramList>(`${this.baseUrl}/programs/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgPrograms }
        });
    }

    getOrgProgramsWithParams(orgId: number, params: HttpParams): Observable<GetOrgProgramList> {
        return this.http.get<GetOrgProgramList>(`${this.baseUrl}/programs/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgProgramsWithParams },
            params: params
        });
    }

    getReminderHistoryOrgPrograms(orgId: number): Observable<GetOrgProgramList> {
        return this.http.get<GetOrgProgramList>(`${this.baseUrl}/programs/history/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReminderHistoryOrgPrograms }
        });
    }

    getReminderHistoryOrgProgramsWithParams(orgId: number, params: HttpParams): Observable<GetOrgProgramList> {
        return this.http.get<GetOrgProgramList>(`${this.baseUrl}/programs/history/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReminderHistoryOrgProgramsWithParams },
            params: params
        });
    }

    getHistoryOrgProgramByRef(orgId: number, programRef: string, logDate: string): Observable<ProgramResponse> {
        return this.http.get<ProgramResponse>(`${this.baseUrl}/programs/${orgId}/${programRef}/${logDate}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getHistoryOrgProgramByRef }
        });
    }

    getOrgProgramsByID(orgId: number, programID: string): Observable<ProgramResponse> {
        return this.http.get<ProgramResponse>(`${this.baseUrl}/programs/${orgId}/${programID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgProgramsByID }
        });
    }

    getOrgProgramByRef(orgId: number, programRef: string): Observable<ProgramResponse> {
        return this.http.get<ProgramResponse>(`${this.baseUrl}/programs/${orgId}/${programRef}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgProgramByRef }
        });
    }

    // Add program
    addProgram(newProgram: ProgramRequest): Observable<ProgramResponse> {
        return this.http.post<ProgramResponse>(`${this.baseUrl}/programs`, newProgram, {
            headers: { 'method-name': AnalyticAPIReqLabels.addProgram }
        });
    }
    // update program
    updateProgram(program_ref: string, modifiedProgram: ProgramRequest): Observable<ProgramResponse> {
        return this.http.put<ProgramResponse>(`${this.baseUrl}/programs/${program_ref}`, modifiedProgram, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateProgram }
        });
    }
    // delete program
    deleteProgram(program_ref: string): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrl}/programs/${program_ref}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteProgram }
        });
    }

    //******************** REMINDER ALERTS FOR PROGRAMS ********************//
    getReminders(orgId: number, program_ref: string): Observable<GetRemindersList> {
        return this.http.get<GetRemindersList>(`${this.baseUrl}/reminders/${orgId}/${program_ref}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReminders }
        });
    }

    getRemindersWithParams(orgId: number, program_ref: string, params: HttpParams): Observable<GetRemindersList> {
        return this.http.get<GetRemindersList>(`${this.baseUrl}/reminders/${orgId}/${program_ref}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getRemindersWithParams },
            params: params
        });
    }

    // Add Reminder
    addReminder(newReminder: ReminderRequest): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/reminders`, newReminder, {
            headers: { 'method-name': AnalyticAPIReqLabels.addReminder }
        });
    }

    // Enable/Disable Reminder
    updateReminder(alertId: number, active: boolean): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(
            `${this.baseUrl}/reminders/${alertId}/${active}`,
            {},
            { headers: { 'method-name': AnalyticAPIReqLabels.updateReminder } }
        );
    }

    //************************* SURVEY ALERTS ******************************//
    getOrgSurveys(orgId: number, params?: HttpParams): Observable<GetSurveyList> {
        return this.http.get<GetSurveyList>(`${this.baseUrl}/survey/${orgId}`, {
            headers: {
                'method-name': params ? AnalyticAPIReqLabels.getOrgSurveysWithParams : AnalyticAPIReqLabels.getOrgSurveys
            },
            params: params
        });
    }

    getOrgSurveysByID(orgId: number, surveyID: number): Observable<SurveyResponse> {
        return this.http.get<SurveyResponse>(`${this.baseUrl}/survey/${orgId}/${surveyID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgSurveysByID }
        });
    }

    // Get Survey response details
    getSurveyResponseByDates(instanceID: string): Observable<GeneralDataResponse<string[]>> {
        return this.http.get<GeneralDataResponse<string[]>>(`${this.baseUrl}/survey/reponses/${instanceID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyResponseByDates }
        });
    }

    // Add Survey
    addSurvey(newSurvey: SurveyRequest): Observable<SurveyResponse> {
        return this.http.post<SurveyResponse>(`${this.baseUrl}/survey`, newSurvey, {
            headers: { 'method-name': AnalyticAPIReqLabels.addSurvey }
        });
    }

    // update Survey
    updateSurvey(surveyID: number, modifiedSurvey: SurveyRequest): Observable<SurveyResponse> {
        return this.http.put<SurveyResponse>(`${this.baseUrl}/survey/${surveyID}`, modifiedSurvey, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateSurvey }
        });
    }

    // delete Survey
    deleteSurvey(surveyID: number): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrl}/survey/${surveyID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteSurvey }
        });
    }

    //************************* SURVEY SCHEDULED ******************************//

    getOrgScheduledSurveys(orgId: number, params?: HttpParams): Observable<GeneralListDataResponse<SurveyScheduled>> {
        return this.http.get<GeneralListDataResponse<SurveyScheduled>>(`${this.baseUrl}/survey/scheduled/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgScheduledSurveys },
            params: params
        });
    }

    // update SCHEDULED Survey
    updateScheduledSurvey(surveyID: number, modifiedSurvey: SurveyScheduledRequest): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(`${this.baseUrl}/survey/scheduled/${surveyID}`, modifiedSurvey, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateScheduledSurvey }
        });
    }

    // delete SCHEDULED Survey
    deleteScheduledSurvey(surveyID: number): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrl}/survey/scheduled/${surveyID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteScheduledSurvey }
        });
    }

    //************************* SURVEY QUESTIONS ******************************//
    getSurveyQuestions(surveyId: number, params?: HttpParams): Observable<GetSurveyQuestionsList> {
        return this.http.get<GetSurveyQuestionsList>(`${this.baseUrl}/surveyquestion/${surveyId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyQuestions },
            params: params
        });
    }

    getSurveyQuestionsByLanguage(surveyId: number, lang: string, params?: HttpParams): Observable<GetSurveyQuestionsList> {
        return this.http.get<GetSurveyQuestionsList>(`${this.baseUrl}/surveyquestion/${surveyId}/${lang}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyQuestionsByLanguage },
            params: params
        });
    }

    //Get Survey Questions Count
    getSurveyQuestionsResponse(instanceId: string): Observable<SurveyQuestionResponses> {
        return this.http.get<SurveyQuestionResponses>(`${this.baseUrl}/survey/dashboardquestion/${instanceId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyQuestionsResponse }
        });
    }

    // Add Survey Questions
    addSurveyQuestions(newSurveyQuestion: SurveyQuestionRequest): Observable<SurveyQuestionResponse> {
        return this.http.post<SurveyQuestionResponse>(`${this.baseUrl}/surveyquestion`, newSurveyQuestion, {
            headers: { 'method-name': AnalyticAPIReqLabels.addSurveyQuestions }
        });
    }

    // update Survey Questions
    updateSurveyQuestions(surveyQID: number, modifiedSurveyQuestion: SurveyQuestionRequest): Observable<SurveyQuestionResponse> {
        return this.http.put<SurveyQuestionResponse>(`${this.baseUrl}/surveyquestion/${surveyQID}`, modifiedSurveyQuestion, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateSurveyQuestions }
        });
    }

    // update Survey Questions Orders
    updateSurveyQuestionsOrder(surveyQID: number, lang: string, orderList: SurveyQuestionOrderRequestModel): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(`${this.baseUrl}/surveyquestion/${surveyQID}/${lang}`, orderList, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateSurveyQuestionsOrder }
        });
    }

    // delete Survey Questions
    deleteSurveyQuestions(surveyQID: number): Observable<GeneralResponse> {
        return this.http.delete<GeneralResponse>(`${this.baseUrl}/surveyquestion/${surveyQID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteSurveyQuestions }
        });
    }

    //************************* SURVEY V2 QUESTIONS ******************************//
    getSurveyQuestionsV2(orgId: number, surveyId: number, workflowValidation?: boolean, workflowId?: number | null): Observable<GeneralDataResponse<SurveyConditionalLogicModel>> {
        let url = `${this.baseUrlV2}/surveyquestion/${orgId}/${surveyId}`;
        if (workflowValidation) {
            url += '?workflowValidation=true';

            if (workflowId) {
                url += `&workflowId=${workflowId}`;
            }
        }
        return this.http.get<GeneralDataResponse<SurveyConditionalLogicModel>>(url, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyQuestionsV2 }
        });
    }

    // Add Survey Questions
    addSurveyQuestionsV2(newSurveyQuestion: SurveyConditionalLogicModel): Observable<GeneralDataResponse<SurveyConditionalLogicModel>> {
        return this.http.post<GeneralDataResponse<SurveyConditionalLogicModel>>(`${this.baseUrlV2}/surveyquestion`, newSurveyQuestion, {
            headers: { 'method-name': AnalyticAPIReqLabels.addSurveyQuestionsV2 }
        });
    }

    // update Survey Questions
    updateSurveyQuestionsV2(
        modifiedSurveyQuestion: SurveyConditionalLogicModel
    ): Observable<GeneralDataResponse<SurveyConditionalLogicModel>> {
        return this.http.put<GeneralDataResponse<SurveyConditionalLogicModel>>(`${this.baseUrlV2}/surveyquestion`, modifiedSurveyQuestion, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateSurveyQuestionsV2 }
        });
    }

    // delete Survey Questions
    deleteSurveyQuestionsV2(orgId: number, surveyId: number): Observable<GeneralDataResponse<SurveyConditionalLogicModel>> {
        return this.http.delete<GeneralDataResponse<SurveyConditionalLogicModel>>(`${this.baseUrlV2}/surveyquestion/${orgId}/${surveyId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteSurveyQuestionsV2 }
        });
    }

    getSurveyV2QuestionsByLanguage(surveyId: number, language: string): Observable<GeneralDataResponse<SurveyQuestionFormModel>> {
        return this.http.get<GeneralDataResponse<SurveyQuestionFormModel>>(
            `${this.baseUrl}/mock_api/surveyquestion_v2/${surveyId}/${language}`
        );
    }

    saveSurveyV2Questions(data: SurveyQuestionFormModel): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/mock_api/surveyquestion_v2`, data);
    }

    //************************* SURVEY EXECUTION ******************************//
    sendSurvey(orgId: number, surveyID: number, body: SurveySendRequest): Observable<SurveySendResponse> {
        return this.http.post<SurveySendResponse>(`${this.baseUrl}/survey/${orgId}/${surveyID}`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.sendSurvey }
        });
    }

    //************************* SURVEY HISTORY ******************************//
    getSurveyHistory(orgId: number, params?: HttpParams, isRefresh: boolean = false): Observable<GetSurveyHistory> {
        return this.http.get<GetSurveyHistory>(`${this.baseUrl}/surveyhistory/${orgId}`, {
            headers: { 'method-name': isRefresh ? AnalyticAPIReqLabels.getSurveyHistoryRefresh : AnalyticAPIReqLabels.getSurveyHistory },
            params: params
        });
    }
    getSurveyHistoryDetail(orgId: number, instanceId: string, payload: PreviewStartDatePayload): Observable<GetSurveyHistoryDetail> {
        return this.http.post<GetSurveyHistoryDetail>(`${this.baseUrl}/surveyhistory/${orgId}/${instanceId}`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyHistoryDetail }
        });
    }
    getSurveyInstanceHistoryDetail(instanceId: string): Observable<GetSurveyInstanceHistoryDetail> {
        return this.http.get<GetSurveyInstanceHistoryDetail>(`${this.baseUrl}/surveyhistory/instances/${instanceId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyInstanceHistoryDetail }
        });
    }

    //Count Survey Re-runs
    getSurveyRerunsCount(instanceID: string): Observable<GeneralDataResponse<SurveyRerunCountsData>> {
        return this.http.get<GeneralDataResponse<SurveyRerunCountsData>>(`${this.baseUrl}/surveyhistory/count-reruns/${instanceID}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getSurveyRerunsCount }
        });
    }

    sendSurveyHistoryRerun(orgId: number, instanceId: string): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(
            `${this.baseUrl}/surveyhistory/rerun/${orgId}/${instanceId}`,
            {},
            { headers: { 'method-name': AnalyticAPIReqLabels.sendSurveyHistoryRerun } }
        );
    }
    getSurveyResponses(orgId: number, instanceId: string) {
        return this.http.post(
            `${this.baseUrl}/surveyhistory/download/${orgId}/${instanceId}`,
            {},
            { headers: { 'method-name': AnalyticAPIReqLabels.getSurveyResponses }, responseType: 'blob' as 'json' }
        );
    }

    getSurveySentUsers(surveyInstanceId: string, params?: HttpParams): Observable<GeneralListDataResponse<SurveySentUser>> {
        return this.http.get<GeneralListDataResponse<SurveySentUser>>(`${this.baseUrl}/survey/sentusers/${surveyInstanceId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getBroadcastSentUsers },
            params: params
        });
    }

    getUpcomingScheduledSurveys(orgId: number, surveyId: number): Observable<GetUpcomingScheduledSurveyResponse> {
        return this.http.get<GetUpcomingScheduledSurveyResponse>(`${this.baseUrl}/survey/checkScheduledSurvey/${orgId}/${surveyId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUpcomingScheduledSurveys }
        });
    }

    //************************* REFERRALS ******************************//
    getReferrals(orgId: number, reqPayload: ListFilterPayload = {}): Observable<GeneralListDataResponse<Referral>> {
        return this.http.post<GeneralListDataResponse<Referral>>(`${this.baseUrl}/referrals/${orgId}`, reqPayload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferrals }
        });
    }

    getReferralById(orgId: number, refId: string, params?: HttpParams): Observable<GeneralDataResponse<ReferralDetails>> {
        return this.http.get<GeneralDataResponse<ReferralDetails>>(`${this.baseUrl}/referrals/${orgId}/${refId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferralById },
            params: params
        });
    }

    getReferralNeedCategory(): Observable<GeneralDataResponse<ReferralNameID[]>> {
        return this.http.get<GeneralDataResponse<ReferralNameID[]>>(`${this.baseUrl}/needs`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferralNeedCategory }
        });
    }

    getReferralNeedByID(categoryId: number, searchValue?: string): Observable<GeneralDataResponse<ReferralNameID[]>> {

        let params = new HttpParams();
        if (searchValue) {
            params = params.set('searchText', searchValue);
        }

        return this.http.get<GeneralDataResponse<ReferralNameID[]>>(`${this.baseUrl}/needs/${categoryId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferralNeedByID },
            params: params
        });
    }

    getReferralNeedForFilter(params?: HttpParams): Observable<GeneralDataResponse<ReferralNameID[]>> {
        return this.http.get<GeneralDataResponse<ReferralNameID[]>>(`${this.baseUrl}/needs/filter`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferralNeedForFilter },
            params: params
        });
    }

    getReferredToFilter(orgId: number, params?: HttpParams): Observable<GeneralDataResponse<ReferralNameID[]>> {
        return this.http.get<GeneralDataResponse<ReferralNameID[]>>(`${this.baseUrl}/serviceproviders/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getReferredToFilter },
            params: params
        });
    }

    getServiceProvidersByNeedId(orgId: number, needId: number): Observable<GeneralDataResponse<ReferralServiceProvider[]>> {
        return this.http.get<GeneralDataResponse<ReferralServiceProvider[]>>(`${this.baseUrl}/serviceproviders/${orgId}/${needId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getServiceProvidersByNeedId }
        });
    }

    // register need
    registerNeeds(newReferral: ReferralRequest): Observable<GeneralDataResponse<RegisterNeedResponse>> {
        return this.http.post<GeneralDataResponse<RegisterNeedResponse>>(`${this.baseUrl}/referrals`, newReferral, {
            headers: { 'method-name': AnalyticAPIReqLabels.registerNeeds }
        });
    }

    // submit referral
    submitReferrals(referralId: string, newReferral: ReferralRequest): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(`${this.baseUrl}/referrals/${referralId}`, newReferral, {
            headers: { 'method-name': AnalyticAPIReqLabels.submitReferrals }
        });
    }

    // update referral status
    updateReferralStatus(referralId: string, newReferralStatus: ReferralStatusRequest): Observable<GeneralResponse> {
        return this.http.put<GeneralResponse>(`${this.baseUrl}/referrals/status/${referralId}`, newReferralStatus, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateReferralStatus }
        });
    }

    // update referral 
    updateReferral(referralId: string, referral: ReferralUpdateRequest): Observable<GeneralResponse> {
        return this.http.patch<GeneralResponse>(`${this.baseUrl}/referrals/update/${referralId}`, referral, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateReferral }
        });
    }

    getExternalUserComment(referralId: string, params?: HttpParams): Observable<GeneralDataResponse<ExternalRefCommentData[]>> {
        return this.http.get<GeneralDataResponse<ExternalRefCommentData[]>>(`${this.baseUrl}/referral-notes/${referralId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.addExternalUserComment },
            params: params
        });
    }

    addExternalUserComment(referralId: string, commentData: ExternalRefCommentRequest): Observable<GeneralResponse> {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/referral-notes/${referralId}`, commentData, {
            headers: { 'method-name': AnalyticAPIReqLabels.addExternalUserComment }
        });
    }

    referralEmailValidateCode(payload: ReferralCodeValidatePayload) {
        return this.http.post<ReferralCodeValidateResponse>(`${this.baseUrl}/referral-auth/validateCode`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateReferralStatus }
        });
    }
    referralEmailConfirm(payload: ReferralConfirmPayload) {
        return this.http.post<GeneralResponse>(`${this.baseUrl}/referral-auth/confirmReferral`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateReferralStatus }
        });
    }

    /**
     * Enable or disable Multi-Factor Authentication (MFA)
     * @param mfa true to enable MFA, false to disable
     * @returns Observable with the response message
     */
    enableDisableMFA(mfa: number): Observable<{ message: string }> {
        const url = `${this.baseUrl}/auth/mfa`;
        return this.http.post<{ message: string }>(url, { mfa }, { headers: { 'method-name': AnalyticAPIReqLabels.enableDisableMFA } });
    }

    getUserLabels(orgId: number, params?: HttpParams): Observable<GeneralListDataResponse<UserLabel>> {
        const defaultParams = new HttpParams()
            .set('page', '1')
            .set('limit', '1000000')
            .set('orderBy', 'created_on')
            .set('order', 'DESC');

        const url = `${this.baseUrl}/orgLabels?orgId=${orgId}`;
        return this.http.get<GeneralListDataResponse<UserLabel>>(url, {
            headers: { 'method-name': AnalyticAPIReqLabels.loadUserLabels },
            params: params ?? defaultParams
        });
    }

    getActiveUserLabels(orgId: number) {
        const url = `${this.baseUrl}/active-orgLabels?orgId=${orgId}`;

        return this.http.get<GeneralDataResponse<UserLabel[]>>(url, { headers: { 'method-name': AnalyticAPIReqLabels.loadUserLabels } });
    }

    addUserLabel(payload: { name: string; orgId: number }) {
        const url = `${this.baseUrl}/orgLabels`;

        return this.http.post<GeneralDataResponse<UserLabel>>(url, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.addUserLabel }
        });
    }

    updateUserLabels(payload: { id: number; orgId: number; active: boolean; name?: string }[]) {
        const url = `${this.baseUrl}/orgLabels`;

        return this.http.put<GeneralResponse>(url, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateUserLabels }
        });
    }

    addOrRemoveUserLabels(payload: any, orgId: number): Observable<GeneralDataResponse<any>> {
        return this.http.put<GeneralDataResponse<any>>(`${this.baseUrl}/orgLabels/addOrRemoveUsers?orgId=${orgId}`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.addOrRemoveUserLabels }
        });
    }

    getUsersInLabels(orgId: number, labelId: number, params?: HttpParams): Observable<GeneralListDataResponse<any>> {
        return this.http.get<GeneralListDataResponse<any>>(`${this.baseUrl}/orgLabels/users/${labelId}/organizations/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getUsersInLabel },
            params: params
        });
    }

    getOrgLabelsScheduledCommunication(orgId: number, labeldIds: number[], clientIds: number[] = []): Observable<GeneralDataResponse<any>> {
        let params = new HttpParams()
            .set('orgId', orgId.toString())
            .set('orgLabelIds', labeldIds.join(','));
        if (clientIds.length > 0) params = params.set('clientIds', clientIds.join(','));
        return this.http.get<GeneralDataResponse<any>>(`${this.baseUrl}/orgLabels/scheduledCommunications`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrgLabelsScheduledCommunication },
            params
        });
    }

    getProgramScheduledCommunications(orgId: number, programId: string, clientIds: number[] = []): Observable<GeneralDataResponse<any>> {
        let params = new HttpParams();
        if (clientIds.length > 0) params = params.set('clientIds', clientIds.join(','));
        return this.http.get<GeneralDataResponse<any>>(
            `${this.baseUrl}/selfServiceManagement/programs/${orgId}/${programId}/scheduledCommunications`,
            { headers: { 'method-name': AnalyticAPIReqLabels.getProgramScheduledCommunications }, params }
        );
    }

    /** WORKFLOWS API's */
    getAllWorkflows(params?: HttpParams): Observable<GeneralListDataResponse<WorkflowListRecordModel>> {
        return this.http.get<GeneralListDataResponse<WorkflowListRecordModel>>(`${this.baseUrl}/workflows`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getAllWorkflows },
            params: params
        });
    }

    getWorkflowById(workflowId: number): Observable<GeneralDataResponse<WorkflowDataModel<WfNodeData>>> {
        return this.http.get<GeneralDataResponse<WorkflowDataModel<WfNodeData>>>(`${this.baseUrl}/workflows/${workflowId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getWorkflowById }
        });
    }

    createWorkflow(payload: CreateWorkflowReq): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/workflows`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.createWorkflow }
        });
    }

    updateWorkflow(workflowId: number, payload: CreateWorkflowReq): Observable<GeneralDataResponse<any>> {
        return this.http.put<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/${workflowId}`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateWorkflow }
        });
    }

    patchWorkflow(workflowId: number, payload: PatchWorkflowReq): Observable<GeneralDataResponse<any>> {
        return this.http.patch<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/${workflowId}`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.patchWorkflow }
        });
    }

    deleteWorkflow(workflowId: number): Observable<GeneralDataResponse<any>> {
        return this.http.delete<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/${workflowId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteWorkflow }
        });
    }
    
    testWorkflow(body: TestWorkflowReq): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/test`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.testWorkflow }
        });
    }

    surveyWorkflowExists(surveyId: number): Observable<GeneralDataResponse<WfSurveyWorkflowExists>> {
        return this.http.get<GeneralDataResponse<WfSurveyWorkflowExists>>(`${this.baseUrl}/workflows/surveyWorkflowExists/${surveyId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.surveyWorkflowExists }
        });
    }
    

    needWorkflowExists(needId: number, params?: HttpParams): Observable<GeneralDataResponse<WfSurveyWorkflowExists>> {

        return this.http.get<GeneralDataResponse<WfSurveyWorkflowExists>>(`${this.baseUrl}/workflows/needWorkflowExists/${needId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.needWorkflowExists },
            params: params
        });
    }

    previewWorkflow(body: CreateWorkflowReq): Observable<GeneralDataResponse<WfPublishPreviewReq>> {
        return this.http.post<GeneralDataResponse<WfPublishPreviewReq>>(`${this.baseUrl}/workflows/preview`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.previewWorkflow }
        });
    }

    getWorkflowVersionsById(workflowId: number): Observable<GeneralDataResponse<WorkflowVersionDataModel[]>> {
        return this.http.get<GeneralDataResponse<WorkflowVersionDataModel[]>>(`${this.baseUrl}/workflows/${workflowId}/versions`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getWorkflowVersionsById }
        });
    }

    createWorkflowVersion(payload: CreateWorkflowReq): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/versions`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.createWorkflowVersion }
        });
    }

    activateWorkflowVersion(workflowId: number, versionId: string): Observable<GeneralDataResponse<any>> {
        return this.http.patch<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/${workflowId}/versions/${versionId}`, {}, {
            headers: { 'method-name': AnalyticAPIReqLabels.activateWorkflowVersion }
        });
    }

    deleteWorkflowVersion(workflowId: number, versionId: string): Observable<GeneralDataResponse<any>> {
        return this.http.delete<GeneralDataResponse<any>>(`${this.baseUrl}/workflows/${workflowId}/versions/${versionId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.deleteWorkflowVersion }
        });
    }

    getDuplicateWorkflow(workflowId: number, versionId: string | null = null): Observable<GeneralDataResponse<WfValidationResponse>> {
        return this.http.get<GeneralDataResponse<WfValidationResponse>>(`${this.baseUrl}/workflows/${workflowId}/duplicate`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getDuplicateWorkflow },
            params: versionId ? { versionId } : {}
        });
    }

    disableGuidedTour(tourId: number): Observable<{ message: string }> {
        return this.http.patch<{ message: string }>(
            `${this.baseUrl}/workflows/guidedTours/${tourId}/disable`, {}, {
            headers: { 'method-name': AnalyticAPIReqLabels.disableGuidedTourWorkflow }
        });
    }

    //************************* TABLEAU ROUTES ******************************//

    getTableauJwtToken(payload: any): Observable<TableauJWTResponse> {
        return this.http.post<TableauJWTResponse>(`${this.baseUrl}/tableau/jwt`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getTableauJwtToken }
        });
    }

    validateTableauSite(orgId: string): Observable<{ message: string, data: any }> {
        return this.http.get<{ message: string, data: any }>(`${this.baseUrl}/tableau/site/${orgId}`, {
            headers: { 'method-name': AnalyticAPIReqLabels.validateTableauSite }
        });
    }

    getIntercomAccessToken(payload: IntercomAccessRequest) {
        return this.http.post<IntercomAccessResponse>(`${this.baseUrl}/auth/intercom-token`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.getIntercomAccessToken }
        });
    }
    getDownloadAnalyticsLink(timezoneIdentifier: string, orgId: number, downloadPayload?: WfAnalyticsReportRequest): Observable<Blob> {
        return this.http.post<Blob>(`${this.baseUrl}/workflowanalytics/download`, downloadPayload, {
            headers: {
                'method-name': AnalyticAPIReqLabels.getWorkflowAnalyticsDownloadLink,
                'bloominghealth-org-id': orgId.toString(),
                'timezone-identifier': timezoneIdentifier
            },
            responseType: 'blob' as 'json',
        });
    }

    getOrganizationAddress(orgId: number): Observable<GeneralDataResponse<OrganizationAddressResponse>> {
        return this.http.get<GeneralDataResponse<OrganizationAddressResponse>>(`${this.baseUrl}/dashboardAdmin/${orgId}/address`, {
            headers: { 'method-name': AnalyticAPIReqLabels.getOrganizationAddress }
        });
    }

    updateOrganizationAddress(orgId: number, payload: any): Observable<GeneralDataResponse<any>> {
        return this.http.patch<GeneralDataResponse<any>>(`${this.baseUrl}/dashboardAdmin/${orgId}/address`, payload, {
            headers: { 'method-name': AnalyticAPIReqLabels.updateOrganizationAddress }
        });
    }

    storeImageFile(orgId: number, payload: StoreImageFileRequest): Observable<StoreImageFileResponse> {
        return this.http.post<StoreImageFileResponse>(
            `${this.baseUrl}/dashboardAdmin/organizations/${orgId}/uploadImage`,
            payload,
            { headers: { 'method-name': 'storeImageFile' } }
        );
    }

    uploadOrganizationLogo(orgId: number, imageFile: File): Observable<GeneralDataResponse<any>> {
        const formData: FormData = new FormData();
        formData.append('image', imageFile);
        return this.http.post<GeneralDataResponse<any>>(
            `${this.baseUrl}/upload/logo/${orgId}`,
            formData,
            { headers: { 'method-name': 'uploadOrganizationLogo' } }
        );
    }


    fetchFileFromSignedUrl(signedUrl: string): Observable<ArrayBuffer> {
        return this.http.get(signedUrl, {
            responseType: 'arraybuffer' // Important for binary files (xlsx)
        });
    }

    segmentAnalyzerFullCheck(body: { message: string; languages: string[] }): Observable<GeneralDataResponse<ISegmentAnalyzerData>> {
        return this.http.post<GeneralDataResponse<ISegmentAnalyzerData>>(`${this.baseUrl}/segment-analyzer/full-check`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.segmentAnalyzerFullCheck }
        });
    }

    segmentAnalyzerRiskAccept(body: ISegmentAnalyzerRiskAcceptPayload): Observable<GeneralDataResponse<any>> {
        return this.http.post<GeneralDataResponse<any>>(`${this.baseUrl}/segment-analyzer/risk-accept`, body, {
            headers: { 'method-name': AnalyticAPIReqLabels.segmentAnalyzerRiskAccept }
        });
    }
}
